import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import posthog from 'posthog-js';
import { submitGdprConsentRecord, updateAcceptedCookies, updateDeniedCookies } from '~/store/actions';
import { config } from '~/config';

const SHARED_GDPR_COOKIE_NAME = 'tripenger_gdpr_consent';
const SHARED_GDPR_COOKIE_ACCEPTED_VALUE = 'accepted_v1.0';
const SHARED_GDPR_COOKIE_REJECTED_VALUE = 'rejected_v1.0';
const SHARED_GDPR_COOKIE_DAYS = 365;
const GDPR_CONSENT_VERSION = '1.0';

type GdprConsentChoice = 'accept' | 'reject';

type GoogleConsentModeValue = 'granted' | 'denied';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    tripengerUpdateGoogleConsentMode?: (choice: GdprConsentChoice) => void;
  }
}

export const updateGoogleConsentMode = (choice: GdprConsentChoice) => {
  if (typeof window === 'undefined') return;

  const optionalCookiesConsent: GoogleConsentModeValue = choice === 'accept' ? 'granted' : 'denied';

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtagFallback(...args: any[]) {
    window.dataLayer?.push(args);
  };

  window.gtag('consent', 'update', {
    ad_storage: optionalCookiesConsent,
    ad_user_data: optionalCookiesConsent,
    ad_personalization: optionalCookiesConsent,
    analytics_storage: optionalCookiesConsent,
    functionality_storage: 'granted',
    security_storage: 'granted',
  });
};

export const updatePostHogConsent = (choice: GdprConsentChoice) => {
  if (typeof window === 'undefined') return;

  if (choice === 'accept') {
    posthog.opt_in_capturing();
    return;
  }

  posthog.opt_out_capturing();
};

export const hasAcceptedTrackingConsent = () => (
  typeof document !== 'undefined'
  && document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .includes(`${SHARED_GDPR_COOKIE_NAME}=${SHARED_GDPR_COOKIE_ACCEPTED_VALUE}`)
);

const hasRejectedOptionalCookies = () => (
  typeof document !== 'undefined'
  && document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .includes(`${SHARED_GDPR_COOKIE_NAME}=${SHARED_GDPR_COOKIE_REJECTED_VALUE}`)
);

const setSharedWordPressConsentCookie = (choice: GdprConsentChoice) => {
  if (typeof document === 'undefined') return;

  const expires = new Date();
  expires.setTime(expires.getTime() + SHARED_GDPR_COOKIE_DAYS * 24 * 60 * 60 * 1000);
  const cookieValue = choice === 'accept' ? SHARED_GDPR_COOKIE_ACCEPTED_VALUE : SHARED_GDPR_COOKIE_REJECTED_VALUE;
  document.cookie = `${SHARED_GDPR_COOKIE_NAME}=${cookieValue}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
};

const dispatchConsentEvent = (choice: GdprConsentChoice) => {
  if (typeof window === 'undefined') return;

  if (typeof window.tripengerUpdateGoogleConsentMode === 'function') {
    window.tripengerUpdateGoogleConsentMode(choice);
  } else {
    updateGoogleConsentMode(choice);
  }

  updatePostHogConsent(choice);

  if (typeof window.CustomEvent !== 'function') return;

  window.dispatchEvent(new window.CustomEvent('tripenger:gdpr-consent-change', {
    detail: {
      choice,
      consentVersion: GDPR_CONSENT_VERSION,
      cookieName: SHARED_GDPR_COOKIE_NAME,
      cookieValue: choice === 'accept' ? SHARED_GDPR_COOKIE_ACCEPTED_VALUE : SHARED_GDPR_COOKIE_REJECTED_VALUE,
    },
  }));
};

const style = {
  position: 'fixed',
  boxSizing: 'border-box',
  left: { xs: 16, sm: 'auto' },
  right: { xs: 16, sm: 24 },
  bottom: { xs: 16, sm: 24 },
  width: { xs: 'auto', sm: 390 },
  maxHeight: { xs: 'calc(100dvh - 32px)', sm: 'calc(100dvh - 48px)' },
  overflowY: 'auto',
  bgcolor: 'background.paper',
  border: '1px solid rgba(0, 0, 0, 0.12)',
  borderRadius: '14px',
  boxShadow: '0 18px 50px rgba(0, 0, 0, 0.22)',
  color: '#1f2933',
  p: '22px',
  zIndex: 2000,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
};

const GDPRConsentModal = () => {
  const dispatch = useDispatch();
  const acceptedCookies = useSelector((state: any) => state.app.acceptedCookies);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    if (hasAcceptedTrackingConsent()) {
      updateGoogleConsentMode('accept');
      updatePostHogConsent('accept');
      dispatch(updateAcceptedCookies());
      setOpen(false);
      return;
    }

    if (hasRejectedOptionalCookies()) {
      updateGoogleConsentMode('reject');
      updatePostHogConsent('reject');
      dispatch(updateDeniedCookies());
      setOpen(false);
      return;
    }

    if (acceptedCookies === null) {
      setOpen(true);
    }
  }, [acceptedCookies, dispatch]);

  const handleAccept = () => {
    setSharedWordPressConsentCookie('accept');
    dispatch(updateAcceptedCookies());
    dispatch(submitGdprConsentRecord({ consentVersion: GDPR_CONSENT_VERSION }));
    dispatchConsentEvent('accept');
    setOpen(false);
  };

  const handleDecline = () => {
    setSharedWordPressConsentCookie('reject');
    dispatch(updateDeniedCookies());
    dispatchConsentEvent('reject');
    setOpen(false);
  };

  if (acceptedCookies !== null || hasAcceptedTrackingConsent() || hasRejectedOptionalCookies()) return null;

  if (!open) return null;

  return (
    <Box
      role="dialog"
      aria-modal="false"
      aria-labelledby="gdpr-modal-title"
      sx={style}
    >
      <Typography
        id="gdpr-modal-title"
        component="h2"
        sx={{
          fontSize: 20,
          fontWeight: 700,
          lineHeight: 1.25,
          mb: '10px',
        }}
      >
        Privacy & Cookie Consent
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: 14,
          lineHeight: 1.55,
          mb: '14px',
          '& a': {
            color: '#0f5bd8',
            textDecoration: 'underline',
          },
        }}
      >
        We use essential cookies to keep Tripenger working and optional cookies to understand site usage and improve our services. You can accept optional cookies or continue without them. We also collect and process personal information you submit to us, such as passenger details, as described in our{' '}
        <a href={`${config.app.helpCenterLink}/privacy-policy`} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        {' '}and{' '}
        <a href={`${config.app.helpCenterLink}/cookie-policy`} target="_blank" rel="noopener noreferrer">Cookie Policy</a>.
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: '10px',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleAccept}
          sx={{
            borderRadius: 999,
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1,
            px: '18px',
            py: '12px',
            textTransform: 'none',
          }}
        >
          Accept optional cookies
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleDecline}
          sx={{
            borderColor: '#b8c2cc',
            borderRadius: 999,
            color: '#334e68',
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1,
            px: '18px',
            py: '12px',
            textTransform: 'none',
            '&:hover': {
              borderColor: '#8fa1b3',
              backgroundColor: 'rgba(15, 91, 216, 0.04)',
            },
          }}
        >
          Essential cookies only
        </Button>
      </Box>
    </Box>
  );
};

export default GDPRConsentModal;
